import type * as React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { FullPageLoader } from "@/components/common/full-page-loader";
import type { StaffRole } from "@/lib/api/types";
import { useAuth } from "@/providers/auth-provider";

export function RequireAuth({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") return <FullPageLoader />;
  if (status === "anonymous") {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <>{children}</>;
}

export function RequireAnonymous({ children }: { children: React.ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <FullPageLoader />;
  if (status === "authenticated") return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

const STAFF_RANK: Record<StaffRole, number> = { support: 1, navigator: 2, super_admin: 3 };

export function RequireStaff({
  children,
  minimum = "support",
}: {
  children: React.ReactNode;
  minimum?: StaffRole;
}) {
  const { status, user } = useAuth();
  if (status === "loading") return <FullPageLoader />;
  if (status === "anonymous") return <Navigate to="/login" replace />;

  const role = user?.staffRole;
  if (!role || STAFF_RANK[role] < STAFF_RANK[minimum]) {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
}
